// FU-a: shared Y domain for the Columns fanout. Stack-aware: each child panel runs through the
// SAME pivot + computeDomain math its ChartCanvas will use, so stacked children keep real
// headroom instead of saturating at the raw per-row max (the row-6 top-tick regression vector).
// Keyed on the RESOLVED DEFAULT facet (decompFacet ?? registry default) — per-child knob
// toggles are accepted-stale, same as the previous behavior (spec §7 FU-a).
// Ratio fanouts: ratios are NEVER stackable (project invariant); their registry rows carry
// line facets, so callers resolve stacked=false -> the per-series path.
// Task 9: the returned tuple is the FINAL [min, max] domain (already niceCeil'ed by
// computeDomain on both ends) — consumers must use it verbatim, NOT re-ceil (niceCeil is not
// idempotent: 110 → 120 → 150). Carries the most-negative min across sibling panels, not just
// the max — a fanout with one negative-bar sibling used to clip every panel to [0, max].
// Note: fillGaps/summariseByUnit/runningTotal are skipped — "same math" means pivot +
// computeDomain only; walk rows arrive pre-bucketed at the governed unit, making
// summarise a no-op in practice.
import { pivot as pivotTimeline } from '~/components/Charts/TimelineChart'
import { pivot as pivotAbsolute } from '~/components/Charts/AbsoluteBarChart'
import { computeDomain, type Group, type SeriesPoint } from '~/components/Charts/dataPipeline'
import type { WalkTimelineRow, WalkAbsoluteRow } from '~/api/endpoints/walks'

const DOMAIN_COLOR = { hex: '#000000', opacity: 1 }
// computeDomain only reads `key` — label/color/stackability are irrelevant to the math.
const domainGroups = (keys: string[]): Group[] =>
  keys.map((k) => ({ key: k, label: k, color: DOMAIN_COLOR }))

export function computeSharedDomain(
  panels: WalkTimelineRow[][] | WalkAbsoluteRow[][],
  kind: 'timeline' | 'absolute_bar',
  stacked: boolean,
): [number, number] {
  let lo = 0
  let hi = 0
  for (const panel of panels) {
    if (panel.length === 0) continue
    if (kind === 'timeline') {
      const piv = pivotTimeline(panel as WalkTimelineRow[])
      const [mn, mx] = computeDomain([piv.points], domainGroups(piv.seriesKeys), stacked)
      if (mn < lo) lo = mn
      if (mx > hi) hi = mx
    } else {
      // BarPoint keys by `dim`; computeDomain only reads the series keys, so the `time`
      // alias exists purely to satisfy the SeriesPoint shape (spread last so a hypothetical
      // series key named 'time' would keep its value — computeDomain never reads `time`).
      const piv = pivotAbsolute(panel as WalkAbsoluteRow[])
      const points = piv.points.map((p) => ({ time: p.dim, ...p })) as SeriesPoint[]
      const [mn, mx] = computeDomain([points], domainGroups(piv.seriesKeys), stacked)
      if (mn < lo) lo = mn
      if (mx > hi) hi = mx
    }
  }
  return [lo, hi]
}
